<template>
  <v-row no-gutters>
    <v-col cols="7">
      <v-menu ref="calendar" :close-on-content-click="false" v-model="calendar" bottom right min-width="250px">
        <template v-slot:activator="{ on }">
          <v-text-field
            :dense="dense"
            v-on="on"
            v-model="formattedDate"
            :label="label"
            :hint="hint"
            :placeholder="placeholder"
            persistent-hint
            :error-messages="errors"
            readonly
            :class="fieldClass"
            prepend-inner-icon="$vuetify.icons.values.calendar"
          />
        </template>
        <v-date-picker v-model="localDateValue" no-title scrollable locale @input="calendar = false" />
      </v-menu>
    </v-col>
    <v-col cols="5">
      <v-menu ref="clock" :close-on-content-click="false" v-model="clock" bottom right min-width="250px">
        <template v-slot:activator="{ on }">
          <v-text-field
            :dense="dense"
            v-on="on"
            v-model="formattedTime"
            :label="timeLabel"
            :placeholder="timePlaceholder"
            persistent-hint
            :error-messages="errors"
            readonly
            :clearable="clearable"
            :class="fieldClass"
            prepend-inner-icon="$vuetify.icons.values.clock"
          />
        </template>
        <v-time-picker v-model="localTimeValue" ampm-in-title @input="clock = false" />
      </v-menu>
    </v-col>
  </v-row>
</template>

<script>
import moment from 'moment-timezone'
import { CustomFieldMixin } from '@argon/app/mixins'
import { formatDT } from '@argon/app/helpers'

export default {
  name: 'DateTimeField',
  mixins: [CustomFieldMixin],
  props: {
    timezone: { type: String, default: 'Canada/Eastern' },
    locale: { type: String, default: 'en' },
    utc: { type: Boolean, default: false }
  },
  data: () => ({
    calendar: false,
    dateValue: null,
    clock: false,
    timeValue: null
  }),
  computed: {
    timeLabel() {
      return this.label ? this.$t('at') : null
    },
    timePlaceholder() {
      return this.placeholder ? this.$t('at') : null
    },
    localDateValue: {
      get() {
        return this.dateValue
      },
      set(v) {
        this.dateValue = moment.tz(v, this.usedTimezone).format('YYYY-MM-DD')
        // default to 12:00
        this.localValue = this.timeValue ? `${v} ${this.timeValue}` : `${v} 12:00`
      }
    },
    localTimeValue: {
      get() {
        return this.timeValue
      },
      set(v) {
        this.timeValue = v
        // default to today
        this.localValue = this.dateValue
          ? `${this.dateValue} ${this.timeValue}`
          : `${moment().tz(this.usedTimezone).format('YYYY-MM-DD')} ${v}`
      }
    },
    localValue: {
      get() {
        return this.value
      },
      set(v) {
        let value = moment.tz(v, this.usedTimezone).toISOString() // -> utc
        this.setValue(value)
      }
    },
    usedTimezone() {
      return this.utc ? 'UTC' : this.timezone
    },
    formattedDate() {
      return this.value ? formatDT(this.value, { format: 'LL', tz: this.usedTimezone, locale: this.locale }) : ''
    },
    formattedTime: {
      get() {
        return this.value ? formatDT(this.value, { format: 'hh:mm A', tz: this.usedTimezone, locale: this.locale }) : ''
      },
      set(v) {
        if (!v) {
          this.setValue(null)
        }
      }
    }
  }
}
</script>

<i18n>
{
  "en": {
    "at": "At"
  },
  "fr": {
    "at": "À"
  }
}
</i18n>
